<template>
  <DxPopup
    :visible="visible"
    @update:visible="(val) => $emit('update:visible', val)"
    :drag-enabled="true"
    :close-on-outside-click="false"
    :show-title="true"
    :title="popupTitle"
    :width="quantityModeEnabled ? '80%' : 875"
    height="750"
    :wrapper-attr="{
      class: 'css-v2 gc-popup gc-popup-label-printing',
    }"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
      :template="item.template ? item.template : null"
    />
    <template #content>
      <div class="lp-popup-inner">
        <!-- Loading State -->
        <div v-if="loading" class="lp-popup-loading">
          <DxLoadIndicator></DxLoadIndicator>
        </div>

        <!-- Load Error State -->
        <div v-else-if="loadError" class="lp-popup-error lp-error-load">
          <div class="lp-error-message">{{ loadError }}</div>
        </div>

        <!-- Content -->
        <template v-else>
          <!-- Locked Header Bar -->
          <div class="lp-popup-locked-bar">
            <div class="lp-locked-field">
              <span class="lp-locked-label">Transaction Type</span>
              <span class="lp-locked-value">Goods In</span>
              <!-- <span class="lp-locked-badge">locked</span> -->
            </div>
            <div class="lp-locked-field">
              <span class="lp-locked-label">Transaction #</span>
              <span class="lp-locked-value">{{ transactionNo }}</span>
              <!-- <span class="lp-locked-badge">locked</span> -->
            </div>
            <div class="lp-locked-field">
              <span class="lp-locked-label">Label Template</span>
              <span class="lp-locked-value">{{ screenName }}</span>
            </div>
          </div>

          <!-- Transaction Summary Strip -->
          <div class="lp-popup-tx-summary" v-if="header">
            <div
              v-if="purchaseOrderNo || header.PurchaseOrderNo"
              class="lp-summary-item"
            >
              <span class="lp-summary-label">PO #</span>
              <span class="lp-summary-value">{{
                purchaseOrderNo || header.PurchaseOrderNo
              }}</span>
            </div>
            <div class="lp-summary-item">
              <span class="lp-summary-label">Supplier</span>
              <span class="lp-summary-value">{{ header.SupplierName }}</span>
            </div>
            <div class="lp-summary-item">
              <span class="lp-summary-label">Date</span>
              <span class="lp-summary-value">{{
                new Date().toShortDateString()
              }}</span>
            </div>
            <div class="lp-summary-item">
              <span class="lp-summary-label">Branch / Division</span>
              <span class="lp-summary-value"
                >{{ header.BranchCode }} / {{ header.DivisionCode }}</span
              >
            </div>
          </div>

          <!-- Quantity Mode Toggle (visible only when system supports it) -->
          <div class="lp-qm-toggle-bar">
            <span class="lp-qm-toggle-label">Quantity Mode</span>
            <label class="lp-toggle-switch">
              <input
                type="checkbox"
                v-model="quantityModeEnabled"
                @change="onQuantityModeToggle"
              />
              <span class="lp-toggle-slider"></span>
            </label>

            <!-- Bulk action buttons (visible only when QM is enabled) -->
            <template v-if="quantityModeEnabled">
              <span class="lp-qm-separator">|</span>
              <button
                class="lp-qm-bulk-btn"
                @click="bulkCalcMethod('Proportional')"
              >
                Proportional — Select All
              </button>
              <span class="lp-qm-separator">|</span>
              <button
                class="lp-qm-bulk-btn"
                @click="bulkCalcMethod('PalletSplit')"
              >
                Pallet Split — Select All
              </button>
              <span class="lp-qm-separator">|</span>
              <button class="lp-qm-bulk-btn" @click="bulkClearAll">
                Clear All
              </button>
            </template>
          </div>

          <!-- Save Error Banner -->
          <div v-if="saveError" class="lp-popup-save-error">
            {{ saveError }}
          </div>

          <!-- Grid -->
          <div class="lp-popup-grid-wrapper">
            <GcLabelPrintingGrid
              ref="labelGrid"
              :products="products"
              :quantity-mode="quantityModeEnabled"
              :default-calc-method="defaultCalcMethod"
              @print="onPrint"
              @cancel="onCancel"
            />
          </div>
        </template>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import DxPopup from "devextreme-vue/popup";
import DxLoadIndicator from "devextreme-vue/load-indicator";
import GcLabelPrintingGrid from "../../components/printing/gc-label-printing-grid.vue";
import { LabelPrintingService } from "../../_services/api/purchasing/label-printing.service";
import { getFactorByCode } from "../../_store/helpers";

export default {
  name: "GcPopupLabelPrinting",
  components: {
    DxPopup,
    DxLoadIndicator,
    GcLabelPrintingGrid,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    transactionNo: {
      type: [Number, String],
      required: true,
    },
    transactionType: {
      type: String,
      default: "GoodsIn",
    },
    purchaseOrderNo: {
      type: [Number, String],
      default: null,
    },
  },
  data: function () {
    return {
      products: [],
      header: null,
      screenName: null,
      loading: false,
      loadError: null,
      saveError: null,
      saving: false,
      quantityModeEnabled: false, // user toggle
      defaultCalcMethod: null, // "Proportional" | "PalletSplit" — driven by factor 645
    };
  },
  computed: {
    popupTitle: function () {
      return "Print Labels — Goods In #" + this.transactionNo;
    },
    /**
     * Get Toolbar Items
     * @returns
     */
    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            icon: "close",
            onClick: this.onCancel,
          },
        },
      ];
    },
  },
  watch: {
    visible: function (newVal) {
      if (newVal && !this.products.length) {
        this.loadTransaction();
      }
    },
  },
  created: async function () {
    if (this.visible) {
      await this.$store.dispatch("screenFactors/getFactors", [644, 645]);
      this.loadTransaction();
    }
  },
  methods: {
    // Task 71417 + 71418: sets QM toggle and default calc method on modal open.
    determineInitialState: function (lines) {
      var factor644 = getFactorByCode(644);
      var factor645 = getFactorByCode(645);

      // Factor 645 always governs the default calc method for lines with no prior history
      this.defaultCalcMethod =
        factor645 && factor645.Factorc === "N" ? "PalletSplit" : "Proportional";

      var linesWithPrior = lines.filter(function (l) {
        return l.PriorPrint;
      });

      if (linesWithPrior.length > 0) {
        // Reload: derive QM state from saved CalcMethod values in prior history.
        // AC: at least one Proportional/PalletSplit row → QM on; all Manual rows → QM off.
        var hasQMMethod = linesWithPrior.some(function (l) {
          return (
            l.PriorPrint.CalcMethod === "Proportional" ||
            l.PriorPrint.CalcMethod === "PalletSplit"
          );
        });
        this.quantityModeEnabled = hasQMMethod;
      } else {
        // Initial load: no prior history — use factor 644 (Y = QM on, N = off)
        this.quantityModeEnabled = factor644 ? factor644.Factorc === "Y" : false;
      }
    },
    loadTransaction: function () {
      var self = this;
      this.loading = true;
      this.loadError = null;

      var loadPromise = LabelPrintingService.load(
        this.transactionType,
        this.transactionNo,
      );

      loadPromise
        .then(function (res) {
          self.header = res.data.Header;
          self.screenName = res.data.DefaultTemplate || "GoodsIn Label";
          self.determineInitialState(res.data.Lines || []);
          self.products = self.transformLines(res.data.Lines || []);
        })
        .catch(function (err) {
          self.loadError =
            "Failed to load transaction details. Please close and try again.";
          self.$logger.error("Label Printing Load Error", err);
        })
        .finally(function () {
          self.loading = false;
        });
    },
    transformLines: function (lines) {
      var productMap = {};
      var products = [];
      lines.forEach(function (line) {
        if (!productMap[line.ItemCode]) {
          productMap[line.ItemCode] = {
            ItemCode: line.ItemCode,
            ItemDescription: line.ItemDescription,
            IsBatchControlled: !!line.BatchNumber,
            IsAggregated: false,
            Batches: [],
          };
          products.push(productMap[line.ItemCode]);
        }
        productMap[line.ItemCode].Batches.push({
          BatchNumber: line.BatchNumber || "",
          ManufacturingDate: line.BatchManufacturedDate || null,
          BestBeforeDate:
            line.BatchBestBeforeDate || line.BatchUseByDate || null,
          ReceivedQuantity: line.BatchQuantity,
          Proportional: line.Proportional || false,
          PriorPrint: line.PriorPrint || null,
          CartonsPerPallet: line.CartonsPerPallet || null,
        });
      });
      return products;
    },
    onPrint: function (payload) {
      var self = this;

      if (!this.screenName) {
        this.$alert(
          "No label template is configured for this transaction. Please contact your administrator.",
          "Cannot Print",
          { type: "error" },
        );
        return;
      }

      this.saving = true;
      this.saveError = null;
      self.$emit("showLoadingIndicator", true);

      var savePayload = {
        QuantityMode: self.quantityModeEnabled,
        LabelPrintingLines: payload.lines.map(function (line) {
          var mappedLine = {
            TransactionType: self.transactionType,
            TransactionNo: self.transactionNo,
            ItemCode: line.ItemCode,
            BatchNumber: line.BatchNumber || "",
            LabelType: self.screenName,
            Print: true,
            LabelsToPrint: line.LabelsToPrint,
          };
          if (self.quantityModeEnabled) {
            mappedLine.CalcMethod = line.CalcMethod || "Proportional";
            if (line.LabelPrintingLineLabels) {
              mappedLine.LabelPrintingLineLabels = line.LabelPrintingLineLabels;
            }
          }
          return mappedLine;
        }),
      };
      LabelPrintingService.save(savePayload)
        .then(function () {
          self.$notify("Labels queued for printing", "success");

          self.$emit("showLoadingIndicator", false);
          self.$emit("update:visible", false);
        })
        .catch(function (err) {
          var apiMessage =
            err && err.response && err.response.data ? err.response.data : null;
          self.saveError =
            typeof apiMessage === "string" && apiMessage.length > 0
              ? apiMessage
              : "Labels could not be queued. Please try again.";
          self.$logger.error("Label Printing Save Error", err);
        })
        .finally(function () {
          self.saving = false;
          self.$emit("showLoadingIndicator", false);
        });
    },
    bulkCalcMethod: function (method) {
      var grid = this.$refs.labelGrid;
      if (grid && grid.bulkSetCalcMethod) {
        grid.bulkSetCalcMethod(method);
      }
    },
    bulkClearAll: function () {
      var grid = this.$refs.labelGrid;
      if (grid && grid.bulkClearAll) {
        grid.bulkClearAll();
      }
    },
    onCancel: async function () {
      var self = this;

      // Check grid's actual rows (which have _selected and _printQty)
      var grid = this.$refs.labelGrid;
      if (!grid || !grid.rows) {
        // Grid not ready, close immediately
        this.$emit("update:visible", false);
        return;
      }

      var hasSelections = grid.rows.some(function (product) {
        return product.Batches.some(function (batch) {
          return batch._selected && batch._printQty > 0;
        });
      });

      // If no selections, close immediately
      if (!hasSelections) {
        this.$emit("update:visible", false);
        return;
      }

      // If selections exist, ask for confirmation
      const confirm = await this.$confirm(
        "You have selected items for printing. Close without printing?",
        "Confirm Cancel",
      );

      if (confirm) {
        self.$emit("update:visible", false);
      }
    },
    onQuantityModeToggle: async function () {
      var hasPrintQtyData = this.hasPrintQtyData();
      if (hasPrintQtyData) {
        const confirm = await this.$confirm(
          "You have unsaved Print Qty data. Changing Quantity Mode now will result in loss of this data. Continue?",
          "Confirm Quantity Mode Change",
        );

        if (confirm) {
          // User confirmed — reset data and keep the new mode (already set by v-model)
          this.resetAllPrintQty();
        } else {
          // User cancelled — revert the toggle back to original state
          this.quantityModeEnabled = !this.quantityModeEnabled;
        }
      }
      // If no data: do nothing — v-model already updated the value
    },
    hasPrintQtyData: function () {
      var grid = this.$refs.labelGrid;
      if (!grid || !grid.rows) {
        return false;
      }
      // Only warn if user has actually modified data (not just pre-filled data)
      return grid.rows.some(function (product) {
        return product.Batches.some(function (batch) {
          return (
            batch._userModified &&
            (batch._printQty > 0 ||
              (batch._labels &&
                batch._labels.some(function (label) {
                  return label._printQty > 0;
                })))
          );
        });
      });
    },
    resetAllPrintQty: function () {
      var grid = this.$refs.labelGrid;
      if (!grid || !grid.rows) return;
      grid.rows.forEach(function (product) {
        product.Batches.forEach(function (batch) {
          batch._printQty = 0;
          batch._selected = false;
          if (batch._labels) {
            batch._labels.forEach(function (label) {
              label._printQty = 0;
              label._selected = false;
            });
          }
        });
        product._selected = false;
      });
    },
  },
};
</script>

<style lang="scss">
.gc-popup-label-printing {
  .dx-popup-content {
    padding: 12px !important;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .lp-popup-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: visible;
  }

  .lp-popup-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    background: #fff;
  }

  .lp-popup-error {
    padding: 16px;
    background: #fff;
    flex: 1;
    overflow-y: auto;
  }

  .lp-error-load {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lp-error-message {
    padding: 16px;
    background: #fee2e2;
    border: 1px solid #fca5a5;
    border-radius: 6px;
    color: #7f1d1d;
    font-size: 14px;
    text-align: center;
    max-width: 400px;
  }

  .lp-popup-locked-bar {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    font-size: 13px;
  }

  .lp-locked-field {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .lp-locked-label {
    font-size: 12px;
    color: #667085;
  }

  .lp-locked-value {
    color: #101828;
    font-weight: 500;
    font-size: 13px;
  }

  .lp-locked-badge {
    display: inline-block;
    font-size: 11px;
    color: #d97706;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
  }

  .lp-popup-tx-summary {
    display: flex;
    gap: 24px;
    padding: 10px 16px;
    background: #fff;
    border-bottom: 1px solid #e5e7eb;
    flex-wrap: wrap;
    flex-shrink: 0;
    align-items: center;
    font-size: 13px;
  }

  .lp-summary-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .lp-summary-label {
    color: #6b7280;
    font-size: 12px;
  }

  .lp-summary-value {
    color: #101828;
    font-weight: 500;
    font-size: 13px;
  }

  .lp-popup-save-error {
    padding: 12px 16px;
    background: #fffbeb;
    border-bottom: 1px solid #fcd34d;
    color: #b45309;
    font-size: 13px;
    flex-shrink: 0;
  }

  .lp-qm-toggle-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .lp-qm-toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: #101828;
  }

  .lp-toggle-switch {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    position: relative;
    height: 20px;
    width: 40px;
  }

  .lp-toggle-switch input {
    display: none;
  }

  .lp-toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    border-radius: 20px;
    transition: all 0.3s;
  }

  .lp-toggle-switch input:checked + .lp-toggle-slider {
    background-color: #35baf6;
  }

  .lp-toggle-slider::before {
    content: "";
    position: absolute;
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    border-radius: 50%;
    transition: all 0.3s;
  }

  .lp-toggle-switch input:checked + .lp-toggle-slider::before {
    transform: translateX(20px);
  }

  .lp-qm-separator {
    color: #d0d5dd;
    font-size: 12px;
  }

  .lp-qm-bulk-btn {
    font-size: 11px;
    color: #0369a1;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
    font-family: inherit;
    transition: color 0.15s ease;
  }

  .lp-qm-bulk-btn:hover {
    color: #075985;
  }

  .lp-popup-grid-wrapper {
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}
</style>
